import { AubadeComponent } from "js/front/bootstrap";
import { gsap } from 'gsap';
import { modulo } from 'libs';
import { modularDistance } from 'js/front/tools/math/modular-distance';
import { APP_URL } from 'js/common/AppUrl';
import Vec2 from 'vec2';

class Slide {
	constructor(e, index){
		this.element = e;

		this.index = index;
		this.visible = false;
		this._rotation = 0;
	}

	get code(){
		return this.element.getAttribute('data-code');
	}

	get visible(){ return this._visible; }
	set visible(visible){
		if(this._visible != visible){
			this._visible = visible;
			this.element.classList.toggle('hidden', !visible);
		}
	}

	get active(){ return this._active; }
	set active(active){
		if(this._active != active){
			this._active = active;
		}
	}

	get title(){
		return this.element.children[0].children[0].innerHTML;
	}

	get rotation(){
		return this._rotation;
	}
	set rotation(rotation){
		this._rotation = rotation;
		gsap.set(this.element, { rotate: (rotation - Math.PI/2) * 180 / Math.PI })
	}
}

class ProductSelectorMobile extends AubadeComponent{
	constructor(e, b){
		super(e, b);

		this.touchStart = this.touchStart.bind(this)
		this.touchEnd 	= this.touchEnd.bind(this)
		this.touchMove 	= this.touchMove.bind(this)

		this.circle	 		  = this.element.querySelector(".fps-circle");
		this.productTitle	  = this.element.querySelector(".fps-current-title");
		this.container 		  = this.element.querySelector(".fps-products-container");
		this.cursor 		  = this.element.querySelector(".fps-active-border");
		this.slides 		  = [];

		this.current = null;
		this.target  	= 0;

		this.touchdown = false;
		this.origin = new Vec2();
		this.mouse 	= new Vec2();
		this.start 	= new Vec2();
		this.end 	= new Vec2();
		this.theta  = 0;

		this.damper = 0.1;

		this.rotationSpeed 		  = 0;
		this.averageRotationSpeed = 0;
		this.averageRotationSpeedSamples = 2;
        this.deltaTime   = 0.016;

		this.startValue = 0;

		this.itemAngle = Math.PI/7
	}

	attach() {
		this.element.addEventListener('touchstart', this.touchStart)
		this.element.addEventListener('mousedown', this.touchStart)
		this.viewport.on('resize', this.resize);
	}

	detach() {
		this.clear();

		this.viewport.off('resize', this.resize);
		this.element.removeEventListener('touchstart', this.touchStart)
		this.element.removeEventListener('mousedown', this.touchStart)
	}

	get size(){
		return this.slides.length;
	}
	get index(){
		return Math.round(this.target/this.itemAngle) || 0;
	}
	set index(index){
		index = Math.max(Math.min(index, this.slides.length-1), 0);
		this.target = index * this.itemAngle;

		if(this.lastCode != this.code){
			this.setTitle(this.slides[this.index] ? this.slides[this.index].title : '');
			this.lastCode = this.code;
			this.emit('change', this.code)
		}
	}

	get code(){
		return this.slides[this.index].code;
	}
	set code(code){
		const index = this.slides.findIndex(slide => slide.code == code);
		if(index > -1) this.index = index;
		this.current = this.target;
		this.render();
	}

	resize = () => {
		this.bounds = this.element.getBoundingClientRect();
		this.origin.set(this.bounds.left + this.bounds.width/2, this.bounds.top + this.bounds.height);
		this.current = this.target;
		this.render();
	}


	tick = () => {
		this.renderTimer = requestAnimationFrame(this.tick)

		this.render()
	}

	render = () => {
		this.current += (this.target - this.current) * this.damper;

		this.averageRotationSpeed = (this.averageRotationSpeed * (this.averageRotationSpeedSamples-1) + this.rotationSpeed) / this.averageRotationSpeedSamples;
        
		this.slides.forEach((slide, i) => {
			slide.rotation = -this.current + i * this.itemAngle;
			slide.visible = slide.rotation >= -this.itemAngle * 2.5 && slide.rotation <= this.itemAngle * 2.5 ;
		});
	}


	onSlideClick = (e) => {
		this.index = this.slides.findIndex(slide => slide.element == e.currentTarget);
	}

	updateMouse(e) {
		if(e.clientX && e.clientY){
			this.mouse.set(e.clientX, e.clientY);
			return;
		}
		if(e.changedTouches && e.changedTouches.length) {
			this.mouse.set(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
			return;
		}
		this.mouse.set(0,0);
	}

	touchStart(e) {
		this.theta = 0;
		this.rotationSpeed = 0;

		this.updateMouse(e);
		this.start.set(this.mouse).subtract(this.origin).multiply(1, -1);
		this.startValue = this.target;

		window.addEventListener('mousemove', this.touchMove);
		window.addEventListener('touchmove', this.touchMove);

		window.addEventListener('touchend', this.touchEnd, true)
		window.addEventListener('mouseup', this.touchEnd, true)
	}
	touchEnd(e) {
		this.theta = 0;
		this.rotationSpeed = 0;

        let time     = 1 / this.deltaTime;
        let tao      = 1 / this.damper;
        let delta 	 = (this.averageRotationSpeed / this.deltaTime * tao * (1 - Math.exp(-time / tao))) * this.deltaTime;

        this.index = Math.round((this.current + delta) / this.itemAngle);

		if(delta > 0.1){
			e.preventDefault();
		}

		window.removeEventListener('touchend', this.touchEnd, true)
		window.removeEventListener('mouseup', this.touchEnd, true)

		window.removeEventListener('mousemove', this.touchMove)
		window.removeEventListener('touchmove', this.touchMove)
	}

	touchMove(e) {
		this.updateMouse(e);
		this.end.set(this.mouse).subtract(this.origin).multiply(1, -1);

		const theta = this.start.angleTo(this.end);

		this.rotationSpeed = theta - this.theta;
		this.theta = theta;

		this.target = this.startValue + this.theta;
		this.clampTarget();
	}

	clampTarget(){
		this.target = Math.max(Math.min(this.target, (this.slides.length - 1) * this.itemAngle), 0);
	}

	fill(products) {
		this.clear();

		const items = [];
		products.forEach(product => {
			const item = document.createElement('div');
			item.classList.add('fps-product', 'fps-slide');
			item.setAttribute('data-code', product.code);

			const inner = document.createElement('div');
			inner.classList.add('fps-product-inner');
			if(product.image)
				inner.style.background = `url(${APP_URL}/storage/images/40x40-${product.image.filename})`;

			const name = document.createElement('div');
			name.classList.add('fps-product-name');
			name.innerHTML = product.color;

			item.appendChild(inner);
			inner.appendChild(name);

			items.push(item);

		});

		this.slides = items.map((item, i) => new Slide(item, i));

		this.target = 0;
		this.current = 0;

		this.slides.forEach((slide, i) => {
			const element = slide.element;
			element.addEventListener('click', this.onSlideClick);
			this.container.appendChild(element);
		});
	}

	clear(){

		this.slides.forEach(slide => {
			const element = slide.element;
			element.removeEventListener('click', this.onSlideClick);
			this.container.removeChild(element);	
		})
	}



	getVisibleSlides(){
		return this.slides.filter((slide, i) => slide.visible);
	}

	setTitle = (newTitle) => {
		if(this.titleTm)
			this.titleTm.kill();

		const updateTitle = () => this.productTitle.innerHTML = newTitle;

		if(this.visible){
			const t = gsap.timeline();
			t.to(this.productTitle, {opacity:0, duration:0.2}, 0)
			t.add(updateTitle);
			t.to(this.productTitle, {opacity:1, duration:0.3});
			this.titleTm = t;
		}
		else updateTitle();

	}

	open(){
		const t = gsap.timeline();
		if(!this.visible){
			this.resize();

			t.fromTo(this.cursor, {scale:0.5}, {scale:1, ease:'quint.out', duration:1}, 0);
			t.fromTo(this.cursor, {opacity:0}, {opacity:1, ease:'none', duration:0.5}, 0);

			t.fromTo(this.circle, {rotate:180}, {rotate:0, ease:'quint.out', duration:1}, 0);

			this.slides.forEach(slide => gsap.set(slide.element, {scale:1}))
			this.getVisibleSlides().forEach(slide => {
				const delta = slide.index - this.index;
				if(delta == 0){
					t.fromTo(slide.element, {opacity:0}, {opacity:1, ease:'none', duration:0.5}, 0);
				}
				else{
					t.from(slide, {rotation:slide.rotation + Math.sign(delta) * Math.PI/2, ease:'quint.out', duration:1}, Math.abs(delta)*0.1);
					t.fromTo(slide.element, {opacity:0}, {opacity:1, ease:'none', duration:0.5}, Math.abs(delta)*0.1);
				}
			});

			t.fromTo(this.productTitle, {opacity: 0}, {opacity: 1, duration: 0.3}, 0.5);

			t.add(this.tick)
			this.visible = true;
		}

		return t;
	}
	close(){
		const t = gsap.timeline();
		if(this.visible){
			this.visible = false;
			cancelAnimationFrame(this.renderTimer);

			t.to(this.cursor, {scale:0.5, ease:'quint.in', duration:0.5}, 0.2);
			t.to(this.cursor, {opacity:0, ease:'none', duration:0.5}, 0.2);

			t.to(this.circle, {rotate:-180, ease:'quint.in', duration:0.5}, 0);
			t.to(this.productTitle, {opacity: 0, duration: 0.3}, 0);

			this.getVisibleSlides().forEach(slide => {
				const delta = slide.index - this.index;
				if(delta == 0){
					t.to(slide.element, {opacity:0, ease:'none', duration:0.5}, 0.2);
				}
				else{
					t.to(slide, {rotation:slide.rotation + Math.sign(delta) * Math.PI/2, ease:'quint.in', duration:0.5}, (2-Math.abs(delta))*0.1);
					t.to(slide.element, {opacity:0, ease:'none', duration:0.5}, (2-Math.abs(delta))*0.1);
				}
			});
		}
		return t;
	}
}

export { ProductSelectorMobile };